import { BlockDiagramViewSettings } from '../types';
import {
  CompositeLayoutData,
  EdgeLabelPreferredPlacement,
  GenericLayoutData,
  GraphComponent,
  HierarchicalLayout,
  HierarchicalLayoutConfig,
  HierarchicalLayoutData,
  HierarchicalLayoutEdgeDescriptor,
  HierarchicalLayoutRoutingStyle,
  IEdge,
  IGroupBoundsCalculator,
  ILabel,
  Insets,
  LayoutDescriptor,
  LayoutKeys,
  LayoutOrientation,
  PortAdjustmentPolicy,
  Size,
  TimeSpan,
} from '@ardoq/yfiles';
import {
  CollapsibleGraphGroup,
  Rect,
  inside,
  type RelationshipDiagramViewModel,
  GRAPH_INSETS,
} from '@ardoq/graph';
import { processDuplicateLabels, groupEdges } from '@ardoq/graph/yfiles';
import { WILDCARD } from '@ardoq/common-helpers';
import { getCriticalPaths } from './criticalPaths';
import {
  getNewIdsTargetedByNewIds,
  incrementalEdgeHint,
  incrementalNodeHint,
  toEdgeEndpoints,
} from './incrementalLayoutHints';
import { isContextNode } from 'yfilesExtensions/styles/nodeDecorator';
import { maximumMarkerWidth } from 'svg/definitions/consts';
import { maximumGraphLayoutDuration } from 'tabview/relationshipDiagrams/maximumGraphLayoutDuration';

const ANIMATION_DURATION = 500;

const standardHierarchicLayoutOptions: HierarchicalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  minimumLayerDistance: 35,
  groupLayeringPolicy: 'recursive',
  fromScratchLayeringStrategy: 'hierarchical-optimal',
};

const layoutOrientationString = (layoutOrientation: LayoutOrientation) => {
  switch (layoutOrientation) {
    case LayoutOrientation.TOP_TO_BOTTOM:
      return 'top-to-bottom';
    case LayoutOrientation.BOTTOM_TO_TOP:
      return 'bottom-to-top';
    case LayoutOrientation.LEFT_TO_RIGHT:
      return 'left-to-right';
    case LayoutOrientation.RIGHT_TO_LEFT:
      return 'right-to-left';
  }
};
const getLayoutInfo = (
  nodeCount: number,
  edgeCount: number,
  layoutOrientation: LayoutOrientation
) => {
  const layoutConfig: HierarchicalLayoutConfig = {
    ...standardHierarchicLayoutOptions,
    stopDuration: TimeSpan.fromMilliseconds(
      maximumGraphLayoutDuration(nodeCount, edgeCount)
    ),
    layoutOrientation: layoutOrientationString(layoutOrientation),
    defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
      backLoopRouting: false,
      routingStyleDescriptor: {
        defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
      },
      minimumFirstSegmentLength: maximumMarkerWidth,
      minimumLastSegmentLength: maximumMarkerWidth,
      minimumLength: 2 * maximumMarkerWidth,
    }),
  };
  const layoutDescriptor: LayoutDescriptor = {
    name: 'HierarchicalLayout',
    properties: layoutConfig,
  };

  return layoutDescriptor;
};

const DEFAULT_PLACEMENT_DESCRIPTOR = new EdgeLabelPreferredPlacement();
const configureLayout = (
  graphComponent: GraphComponent,
  {
    nodes,
    groups,
    edges,
    expandedStateChangingGroupId,
  }: RelationshipDiagramViewModel,
  {
    layoutOrientation,
    separateReferences,
  }: Pick<BlockDiagramViewSettings, 'layoutOrientation' | 'separateReferences'>
) => {
  const nodeCount = nodes.add.length + nodes.update.length;
  const edgeCount = edges.add.length + edges.update.length;

  const layoutDescriptor = getLayoutInfo(
    nodeCount,
    edgeCount,
    layoutOrientation
  );

  const isUpdate =
    nodes.update.length + groups.update.length + edges.update.length > 0;

  // when expanding or collapsing a group, avoid a distracting re-layout-- the graph layout should be stable, so the user's eye can follow the group as it grows or shrinks.
  // for other updates, especially toggling "separate references," a from-scratch layout is preferred, so that edge crossings can be re-evaluated given the newly grouped or ungrouped edges.
  layoutDescriptor.properties!.fromSketchMode = Boolean(
    expandedStateChangingGroupId
  );

  const { graph, contentBounds, viewport } = graphComponent;
  let layoutData: HierarchicalLayoutData;

  if (!separateReferences) {
    const {
      edgeSourceGroup,
      edgeSourceGroups,
      edgeTargetGroup,
      edgeTargetGroups,
    } = groupEdges(graph);

    layoutData = new HierarchicalLayoutData({
      sourceGroupIds: edgeSourceGroup,
      targetGroupIds: edgeTargetGroup,
      edgeCrossingCosts: (edge: IEdge) => {
        const sourceGroupId = edgeSourceGroup(edge) || '';
        const targetGroupId = edgeTargetGroup(edge) || '';

        const sourceGroupEdgesCount =
          edgeSourceGroups.get(sourceGroupId)?.length ?? 0;
        const targetGroupEdgesCount =
          edgeTargetGroups.get(targetGroupId)?.length ?? 0;

        return Math.max(sourceGroupEdgesCount + targetGroupEdgesCount, 1);
      },
    });

    const edgeLabelPreferredPlacement = processDuplicateLabels(graph);
    layoutData.edgeLabelPreferredPlacements.mapperFunction = (label: ILabel) =>
      edgeLabelPreferredPlacement.get(label) ?? DEFAULT_PLACEMENT_DESCRIPTOR;
  } else {
    layoutData = new HierarchicalLayoutData({ edgeDirectedness: 1 });
  }
  const expandingOrCollapsingAll = expandedStateChangingGroupId === WILDCARD;
  if (expandingOrCollapsingAll) {
    graph.nodes.forEach(node => {
      if (graph.isGroupNode(node)) {
        graph.adjustGroupNodeLayout(node);
      }
    });
  } else {
    const expandedStateChangingNode =
      expandedStateChangingGroupId &&
      graph.nodes.find(
        node =>
          node.tag instanceof CollapsibleGraphGroup &&
          node.tag.id === expandedStateChangingGroupId
      );
    if (expandedStateChangingNode) {
      graph.adjustGroupNodeLayout(expandedStateChangingNode);
      graph.groupingSupport
        .getDescendants(expandedStateChangingNode)
        .filter(node => graph.isGroupNode(node))
        .forEach(groupNode => graph.adjustGroupNodeLayout(groupNode));
    }
  }

  const wasFullGraphDisplayed = inside(
    Rect.fromRectLike(contentBounds),
    Rect.fromRectLike(viewport)
  );
  const animateViewport =
    expandedStateChangingGroupId !== null &&
    (wasFullGraphDisplayed || expandingOrCollapsingAll);

  const newIds = new Set([...nodes.add, ...groups.add, ...edges.add]);
  const isTogglingOneGroup =
    expandedStateChangingGroupId !== null && !expandingOrCollapsingAll;
  const incrementallyPlacedNodeIds = isTogglingOneGroup
    ? getNewIdsTargetedByNewIds(
        newIds,
        graph.edges.map(toEdgeEndpoints).toArray()
      )
    : newIds;
  const criticalPaths = getCriticalPaths({
    graph,
    contextNode: graph.nodes.find(isContextNode),
  });
  if (criticalPaths.size) {
    layoutData.criticalEdgePriorities = edge => criticalPaths.get(edge) ?? null;
  }
  const customMappingLayoutData = new GenericLayoutData();
  customMappingLayoutData.addItemMapping(
    HierarchicalLayout.INCREMENTAL_NODE_HINTS_DATA_KEY,
    incrementalNodeHint(incrementallyPlacedNodeIds)
  );
  customMappingLayoutData.addItemMapping(
    HierarchicalLayout.INCREMENTAL_EDGE_HINTS_DATA_KEY,
    incrementalEdgeHint(newIds)
  );
  customMappingLayoutData.addItemMapping(
    LayoutKeys.MINIMUM_GROUP_NODE_SIZE_DATA_KEY,
    groupNode => {
      const boundsProvider = groupNode.lookup(IGroupBoundsCalculator);
      const bounds = boundsProvider?.calculateBounds(graph);
      if (!bounds) {
        return Size.EMPTY;
      }
      return bounds.toSize();
    }
  );

  return {
    layoutDescriptor,
    layoutData: new CompositeLayoutData(layoutData, customMappingLayoutData),
    duration: isUpdate
      ? TimeSpan.fromMilliseconds(ANIMATION_DURATION)
      : TimeSpan.ZERO,
    animateViewport,
    updateContentRect: true,
    targetBoundsInsets: wasFullGraphDisplayed
      ? new Insets(GRAPH_INSETS)
      : Insets.EMPTY,
    portAdjustmentPolicy: PortAdjustmentPolicy.ALWAYS, // shorten or elongate edges according to the outline as defined by ArdoqNodeStyle/IsometricNodeStyle. this prevents the edges from appearing "on top of" the faces of the isometric blocks.
  };
};
export { configureLayout };
